import { useState, useRef, useEffect } from "react";

// Steps for the "Live Project Enrollment" stepper.
const lpeSteps = [
  {
    n: "01",
    title: "Project enrollment",
    sub: "You're matched to a live brief that fits your specialisation and career direction.",
  },
  {
    n: "02",
    title: "Industry-format drawings",
    sub: "Implement what you learn in training, with trainers guiding you to industry-standard drawing sets.",
  },
  {
    n: "03",
    title: "Consultant coordination",
    sub: "Learn how to coordinate with consultants and produce all service drawings.",
  },
  {
    n: "04",
    title: "Handover",
    sub: "Complete your project with all technical drawings and specifications, ready to present.",
  },
];

function LiveProjectSection({ showDaysBadge = true }) {
  const [activeSteps, setActiveSteps] = useState(1);
  const lpeAnimatedRef = useRef(false);
  const lpeTimerRef = useRef(null);
  const revealRef = useRef(null);

  // LPE stepper — runs once on mount (overview section is always mounted now).
  useEffect(() => {
    lpeAnimatedRef.current = false;
    setActiveSteps(1);

    const stepper = revealRef.current;
    if (!stepper) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !lpeAnimatedRef.current) {
            lpeAnimatedRef.current = true;
            let index = 1;
            lpeTimerRef.current = window.setInterval(() => {
              if (index < 4) {
                index += 1;
                setActiveSteps(index);
              } else {
                window.clearInterval(lpeTimerRef.current);
              }
            }, 450);
          }
        });
      },
      { threshold: 0.4 }
    );
    observer.observe(stepper);
    return () => {
      observer.disconnect();
      if (lpeTimerRef.current) window.clearInterval(lpeTimerRef.current);
    };
  }, []);

  return (
    <section className="section lpe">
      <style>{`
        .lpe {
          background: var(--sand);
          position: relative;
          overflow: hidden;
        }
        .lpe .wrap {
          padding-right: 3.5rem;
        }
        .lpe .eyebrow {
          display: flex;
          align-items: center;
          gap: 0.7rem;
          margin-bottom: 1rem;
        }
        .lpe .eyebrow-dash {
          width: 28px;
          height: 1px;
          background: var(--terra);
        }
        .lpe .eyebrow-text {
          font-family: var(--mono);
          font-size: 0.7rem;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          color: var(--terra);
        }
        .lpe-head {
          display: flex;
          justify-content: space-between;
          align-items: flex-end;
          gap: 2rem;
          flex-wrap: wrap;
        }
        .lpe-badge {
          font-family: var(--serif);
          font-size: 3.4rem;
          color: var(--charcoal);
          line-height: 1;
        }
        .lpe-badge small {
          display: block;
          font-family: var(--mono);
          font-size: 0.6rem;
          letter-spacing: 0.16em;
          text-transform: uppercase;
          color: var(--mid);
          opacity: 0.7;
          margin-top: 0.35rem;
        }
        .stepper {
          margin-top: 3rem;
          position: relative;
        }
        .track {
          position: absolute;
          top: 19px;
          left: 0;
          right: 0;
          height: 2px;
          background: rgba(30, 29, 27, 0.15);
        }
        .track-fill {
          height: 100%;
          width: 0;
          background: var(--terra);
          transition: width 0.5s ease;
        }
        .steps {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 1.4rem;
          position: relative;
        }
        .step-dot {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background: var(--sand);
          border: 2px solid rgba(30, 29, 27, 0.2);
          display: flex;
          align-items: center;
          justify-content: center;
          font-family: var(--mono);
          font-size: 0.72rem;
          color: var(--mid);
          transition: 0.4s;
        }
        .step.active .step-dot {
          background: var(--terra);
          border-color: var(--terra);
          color: #fff;
        }
        .step-t {
          font-size: 0.9rem;
          font-weight: 600;
          margin: 1rem 0 0.35rem;
        }
        .step-s {
          font-size: 0.79rem;
          font-weight: 300;
          color: var(--mid);
          line-height: 1.65;
          max-width: 30ch;
        }
        @media (max-width: 820px) {
          .steps {
            grid-template-columns: 1fr 1fr;
          }
          .track {
            display: none;
          }
        }
        @media (max-width: 520px) {
          .steps {
            grid-template-columns: 1fr;
          }
        }
      `}</style>

      <div className="bp" />
      <div className="wrap" style={{ position: "relative" }}>
        <div className="lpe-head rv">
          <div>
            <div className="eyebrow">
              <div className="eyebrow-dash" />
              <span className="eyebrow-text">Live Project Enrollment</span>
            </div>
            {/* <h2 className="sec-title">
              {showDaysBadge ? "45 days. " : ""} <br /> One real project.
              <br />
              <em>Zero simulations.</em>
            </h2> */}

<h2 className="sec-title">
  {showDaysBadge ? "45 days. " : ""}
  <br className="lpe-mobile-br" /> One real project.
  <br />
  <em>Zero simulations.</em>
</h2>

            <p className="sec-sub" style={{ color: "var(--mid)" }}>
              You'll be enrolled onto an active project and carry it the way a
              junior architect does in a real office — drawing, revising,
              coordinating and closing out.
            </p>
          </div>

          {showDaysBadge && (
            <div className="lpe-badge" style={{ paddingRight: "1.5rem" }}>
              45
              <small>Days </small>
            </div>
          )}
        </div>

        <div className="stepper rv d2" ref={revealRef}>
          <div className="track">
            <div
              className="track-fill"
              style={{ width: `${(activeSteps / 4) * 100}%` }}
            />
          </div>
          <div className="steps">
            {lpeSteps.map((step, index) => (
              <div
                className={`step${index < activeSteps ? " active" : ""}`}
                key={step.n}
              >
                <div className="step-dot">{step.n}</div>
                <div className="step-t">{step.title}</div>
                <div className="step-s">{step.sub}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export default LiveProjectSection;